<template>
  <Dialog
    v-model:visible="visible"
    :header="dialogTitle"
    :modal="true"
    :closable="!isGenerating && !isSaving"
    :style="{ width: '80vw', maxHeight: '90vh' }"
    @hide="onHide"
  >
    <!-- Этап 1: Проверка переводов -->
    <div v-if="stage === 'check'" class="translations-check">
      <Message v-if="checkResult" :severity="checkResult.translations_needed ? 'warn' : 'success'">
        <div class="flex flex-column gap-2">
          <div class="font-bold">
            {{ checkResult.translations_needed ?
               `⚠️ Требуется создать ${checkResult.total_missing} переводов` :
               '✅ Все переводы готовы'
            }}
          </div>
          <div v-if="checkResult.translations_needed && checkResult.missing_translations">
            <div class="text-sm">Недостающие переводы по полям:</div>
            <ul class="mt-2 ml-4">
              <li v-for="(values, field) in checkResult.missing_translations" :key="field">
                <strong>{{ fieldLabels[field] || field }}</strong>: {{ values.length }} значений
                <div class="text-xs text-color-secondary mt-1">
                  {{ values.slice(0, 5).join(', ') }}{{ values.length > 5 ? '...' : '' }}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </Message>

      <div v-else class="text-center py-4">
        <ProgressSpinner />
        <div class="mt-3">Проверка переводов...</div>
      </div>
    </div>

    <!-- Этап 2: Генерация переводов -->
    <div v-if="stage === 'generate'" class="translations-generate">
      <Message severity="info">
        <div class="flex align-items-center gap-3">
          <ProgressSpinner style="width: 30px; height: 30px" />
          <div>
            <div class="font-bold">Генерация переводов через OpenAI...</div>
            <div class="text-sm mt-1">Это может занять несколько минут</div>
          </div>
        </div>
      </Message>
    </div>

    <!-- Этап 3: ПРЕДПРОСМОТР переводов (НОВЫЙ ШАГ) -->
    <div v-if="stage === 'preview'" class="translations-preview">
      <Message severity="info" class="mb-3">
        <div class="flex justify-content-between align-items-center">
          <div>
            <div class="font-bold">📋 Предпросмотр переводов</div>
            <div class="text-sm mt-1">
              Сгенерировано {{ previewData.length }} переводов.
              Проверьте корректность и отредактируйте при необходимости.
            </div>
          </div>
          <div class="flex gap-2">
            <Button
              label="Сбросить изменения"
              icon="pi pi-refresh"
              severity="secondary"
              size="small"
              @click="resetPreview"
            />
          </div>
        </div>
      </Message>

      <!-- Editable таблица -->
      <DataTable
        :value="previewData"
        :loading="isLoading"
        scrollable
        scrollHeight="400px"
        :rows="100"
        stripedRows
        size="small"
        editMode="cell"
        @cell-edit-complete="onCellEditComplete"
      >
        <Column field="field" header="Поле" :style="{ width: '15%' }" frozen>
          <template #body="{ data }">
            <Tag :value="fieldLabels[data.field] || data.field" />
          </template>
        </Column>
        <Column field="source_value" header="Оригинал (ru)" :style="{ width: '35%' }">
          <template #body="{ data }">
            <span class="font-semibold">{{ data.source_value }}</span>
          </template>
        </Column>
        <Column field="canonical_value" header="Перевод" :style="{ width: '35%' }">
          <template #body="{ data }">
            <div class="flex align-items-center gap-2">
              <span>{{ getLocaleFlag(store.locale) }}</span>
              <span
                :class="{ 'text-primary font-semibold': data.modified }"
              >
                {{ data.canonical_value }}
              </span>
              <Tag v-if="data.modified" value="Изменён" severity="warning" size="small" />
            </div>
          </template>
          <template #editor="{ data }">
            <InputText
              v-model="data.canonical_value"
              class="w-full"
              autofocus
            />
          </template>
        </Column>
        <Column field="status" header="Статус" :style="{ width: '15%' }">
          <template #body="{ data }">
            <Tag
              :value="data.status === 'new' ? 'Новый' : 'Существует'"
              :severity="data.status === 'new' ? 'success' : 'info'"
            />
          </template>
        </Column>
      </DataTable>

      <div class="mt-3 p-3 surface-100 border-round">
        <div class="flex justify-content-between align-items-center">
          <div class="text-sm">
            <strong>Итого:</strong>
            {{ previewData.filter(t => t.status === 'new').length }} новых переводов,
            {{ previewData.filter(t => t.modified).length }} изменено
          </div>
          <div class="text-xs text-color-secondary">
            💡 Кликните на ячейку перевода для редактирования
          </div>
        </div>
      </div>
    </div>

    <!-- Этап 4: Сохранение -->
    <div v-if="stage === 'save'" class="translations-save">
      <Message severity="success">
        <div class="font-bold">✅ Переводы успешно сохранены!</div>
        <div class="text-sm mt-2">
          Сохранено {{ savedCount }} переводов в {{ savedFields }} полях
        </div>
      </Message>
    </div>

    <!-- Этап 5: Просмотр существующих переводов -->
    <div v-if="stage === 'view'" class="translations-view">
      <DataTable
        :value="translations"
        :loading="isLoading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[10, 25, 50]"
        stripedRows
        size="small"
      >
        <Column field="attribute_key" header="Поле" :style="{ width: '15%' }">
          <template #body="{ data }">
            <Tag :value="fieldLabels[data.attribute_key] || data.attribute_key" />
          </template>
        </Column>
        <Column field="source_value" header="Оригинал (ru)" :style="{ width: '30%' }"></Column>
        <Column field="canonical_value" header="Перевод" :style="{ width: '30%' }">
          <template #body="{ data }">
            <div class="flex align-items-center gap-2">
              <span>{{ getLocaleFlag(store.locale) }}</span>
              <span>{{ data.canonical_value }}</span>
            </div>
          </template>
        </Column>
        <Column field="updated_at" header="Обновлено" :style="{ width: '15%' }">
          <template #body="{ data }">
            {{ formatDate(data.updated_at) }}
          </template>
        </Column>
        <Column header="Действия" :style="{ width: '10%' }">
          <template #body="{ data }">
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              size="small"
              @click="deleteTranslation(data.id)"
              :disabled="isLoading"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <template #footer>
      <div class="flex justify-content-between">
        <div>
          <!-- Кнопка генерации (этап check) -->
          <Button
            v-if="stage === 'check' && checkResult?.translations_needed"
            label="Сгенерировать переводы"
            icon="pi pi-sparkles"
            @click="startGeneration"
            :disabled="isGenerating"
          />

          <!-- Кнопка сохранения (этап preview) -->
          <Button
            v-if="stage === 'preview'"
            label="Сохранить все переводы"
            icon="pi pi-save"
            severity="success"
            @click="savePreview"
            :disabled="isSaving"
            :loading="isSaving"
          />

          <!-- Кнопка просмотра (этап save) -->
          <Button
            v-if="stage === 'save'"
            label="Просмотреть переводы"
            icon="pi pi-list"
            @click="viewTranslations"
          />
        </div>
        <div>
          <!-- Кнопка "Назад" для preview -->
          <Button
            v-if="stage === 'preview'"
            label="Назад"
            icon="pi pi-arrow-left"
            severity="secondary"
            class="mr-2"
            @click="backToCheck"
            :disabled="isSaving"
          />

          <Button
            label="Закрыть"
            icon="pi pi-times"
            @click="visible = false"
            :disabled="isGenerating || isSaving"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Message from 'primevue/message'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import apiService from '@/services/apiService'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  store: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'translations-updated'])

const toast = useToast()

// Состояние диалога
const visible = ref(props.modelValue)
const stage = ref('check') // 'check' | 'generate' | 'preview' | 'save' | 'view'
const isLoading = ref(false)
const isGenerating = ref(false)
const isSaving = ref(false)

// Результаты проверки и генерации
const checkResult = ref(null)
const generateResult = ref(null)
const translations = ref([])

// Данные для предпросмотра
const previewData = ref([])
const previewDataOriginal = ref([]) // Оригинальная копия для сброса
const savedCount = ref(0)
const savedFields = ref(0)

// Метки полей
const fieldLabels = {
  type: 'Тип',
  gender: 'Пол',
  age: 'Возраст',
  shape: 'Форма',
  material: 'Материал',
  category_name: 'Название категории'
}

// Вычисляемые свойства
const dialogTitle = computed(() => {
  const flag = getLocaleFlag(props.store.locale)
  const storeName = props.store.name

  if (stage.value === 'check') {
    return `${flag} Переводы для "${storeName}"`
  } else if (stage.value === 'generate') {
    return `${flag} Генерация переводов для "${storeName}"`
  } else if (stage.value === 'preview') {
    return `${flag} Предпросмотр переводов "${storeName}"`
  } else if (stage.value === 'save') {
    return `${flag} Переводы сохранены "${storeName}"`
  } else {
    return `${flag} Переводы "${storeName}"`
  }
})

// Синхронизация visible с modelValue
watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    checkTranslations()
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

// Методы
async function checkTranslations() {
  stage.value = 'check'
  checkResult.value = null
  isLoading.value = true

  try {
    checkResult.value = await apiService.checkTranslations(props.store.id)
  } catch (error) {
    console.error('Ошибка проверки переводов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось проверить переводы',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

async function startGeneration() {
  stage.value = 'generate'
  generateResult.value = null
  isGenerating.value = true

  try {
    // ГЕНЕРАЦИЯ С PREVIEW=TRUE (БЕЗ СОХРАНЕНИЯ В БД)
    generateResult.value = await apiService.generateTranslations(
      props.store.id,
      null,
      true // preview mode
    )

    if (generateResult.value.status === 'preview' && generateResult.value.translations) {
      // Преобразовать в массив для таблицы
      const translationsArray = []
      for (const [field, values] of Object.entries(generateResult.value.translations)) {
        for (const [source, translation] of Object.entries(values)) {
          translationsArray.push({
            field: field,
            source_value: source,
            canonical_value: translation,
            status: 'new', // Все новые, т.к. это preview
            modified: false
          })
        }
      }

      previewData.value = translationsArray
      previewDataOriginal.value = JSON.parse(JSON.stringify(translationsArray)) // Deep copy

      // Переход к предпросмотру
      stage.value = 'preview'

      toast.add({
        severity: 'success',
        summary: 'Готово',
        detail: `Сгенерировано ${translationsArray.length} переводов для проверки`,
        life: 3000
      })
    }
  } catch (error) {
    console.error('Ошибка генерации переводов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сгенерировать переводы',
      life: 5000
    })
    stage.value = 'check'
  } finally {
    isGenerating.value = false
  }
}

async function savePreview() {
  isSaving.value = true

  try {
    // Преобразовать обратно в формат {field: {source: translation}}
    const translationsToSave = {}
    for (const item of previewData.value) {
      if (!translationsToSave[item.field]) {
        translationsToSave[item.field] = {}
      }
      translationsToSave[item.field][item.source_value] = item.canonical_value
    }

    const result = await apiService.savePreviewTranslations(props.store.id, translationsToSave)

    savedCount.value = result.total_values
    savedFields.value = result.translated_fields

    stage.value = 'save'

    toast.add({
      severity: 'success',
      summary: 'Успешно',
      detail: `Сохранено ${savedCount.value} переводов`,
      life: 5000
    })

    emit('translations-updated')
  } catch (error) {
    console.error('Ошибка сохранения переводов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сохранить переводы',
      life: 5000
    })
  } finally {
    isSaving.value = false
  }
}

function onCellEditComplete(event) {
  const { data, newValue, field } = event

  if (field === 'canonical_value') {
    // ВАЛИДАЦИЯ ПОЛЬЗОВАТЕЛЬСКОГО ВВОДА
    let validatedValue = newValue

    // 1. Проверка на пустое значение
    if (!validatedValue || validatedValue.trim() === '') {
      toast.add({
        severity: 'warn',
        summary: 'Некорректное значение',
        detail: 'Перевод не может быть пустым',
        life: 3000
      })
      // Вернуть оригинальное значение
      const original = previewDataOriginal.value.find(
        t => t.field === data.field && t.source_value === data.source_value
      )
      data.canonical_value = original?.canonical_value || data.canonical_value
      return
    }

    // 2. Обрезать лишние пробелы
    validatedValue = validatedValue.trim()

    // 3. Проверка длины (максимум 255 символов для БД)
    if (validatedValue.length > 255) {
      toast.add({
        severity: 'warn',
        summary: 'Слишком длинное значение',
        detail: 'Максимальная длина перевода: 255 символов',
        life: 3000
      })
      validatedValue = validatedValue.substring(0, 255)
    }

    // 4. Проверка на опасные символы (защита от XSS)
    const dangerousChars = /<script|<iframe|javascript:|onerror=/i
    if (dangerousChars.test(validatedValue)) {
      toast.add({
        severity: 'error',
        summary: 'Недопустимые символы',
        detail: 'Перевод содержит запрещённые символы',
        life: 3000
      })
      // Вернуть оригинальное значение
      const original = previewDataOriginal.value.find(
        t => t.field === data.field && t.source_value === data.source_value
      )
      data.canonical_value = original?.canonical_value || data.canonical_value
      return
    }

    // Применить валидированное значение
    data.canonical_value = validatedValue

    // Отметить как изменённый если отличается от оригинала
    const original = previewDataOriginal.value.find(
      t => t.field === data.field && t.source_value === data.source_value
    )
    data.modified = original && original.canonical_value !== validatedValue
  }
}

function resetPreview() {
  previewData.value = JSON.parse(JSON.stringify(previewDataOriginal.value))
  toast.add({
    severity: 'info',
    summary: 'Сброшено',
    detail: 'Изменения отменены',
    life: 2000
  })
}

function backToCheck() {
  stage.value = 'check'
  previewData.value = []
  previewDataOriginal.value = []
}

async function viewTranslations() {
  stage.value = 'view'
  isLoading.value = true

  try {
    translations.value = await apiService.getTranslations({
      locale: props.store.locale,
      limit: 1000
    })
  } catch (error) {
    console.error('Ошибка загрузки переводов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить переводы',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

async function deleteTranslation(translationId) {
  if (!confirm('Удалить этот перевод?')) return

  isLoading.value = true
  try {
    await apiService.deleteTranslation(translationId)

    // Удалить из локального списка
    translations.value = translations.value.filter(t => t.id !== translationId)

    toast.add({
      severity: 'success',
      summary: 'Удалено',
      detail: 'Перевод удалён',
      life: 3000
    })

    emit('translations-updated')
  } catch (error) {
    console.error('Ошибка удаления перевода:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось удалить перевод',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

function getLocaleFlag(locale) {
  const flags = {
    'ru': '🇷🇺',
    'en': '🇬🇧',
    'it': '🇮🇹',
    'de': '🇩🇪',
    'es': '🇪🇸'
  }
  return flags[locale] || '🌍'
}

function formatDate(dateString) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function onHide() {
  // Сброс состояния при закрытии
  stage.value = 'check'
  checkResult.value = null
  generateResult.value = null
  translations.value = []
  previewData.value = []
  previewDataOriginal.value = []
}
</script>

<style scoped>
.translations-check,
.translations-generate,
.translations-preview,
.translations-save,
.translations-view {
  min-height: 200px;
}

.translations-preview {
  max-height: 600px;
  overflow-y: auto;
}
</style>
